import { useState, type Ref } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JobSetServiceTypeResult } from '@servwell/shared';
import { dash } from '@/components/ui/data-table';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { refetchServiceTypes } from './ServiceTypePicker';

/**
 * What the two places that give EXISTING jobs a service type share (ops insights 08): the Jobs
 * list's "Set service type" on the jobs ticked, and the job page's change on one. Both ask with
 * `ServiceTypeStep`, send `job.setServiceType` through `useSetServiceType`, and say what it did
 * with `SetServiceTypeResult`, so both forget, announce and name alike. Why the set works at any
 * status, is partial, and never clears a type: `setJobServiceType` in apps/api.
 */

/** The picker's words where an existing job is given a type (`ServiceTypeStep`'s `picker`). */
export const EXISTING_JOB_PICKER = {
  // The Jobs list also has "Filter by service type": the two are told apart by name.
  label: 'Set to',
  emptyReason: "There's no service type to give a job: your shop has none in use.",
};

/** A job as a set names it: by the number and title the office sees on screen. */
export type NamedJob = { id: string; jobNumber: number; title: string };

/** "#12 No heat": ONE name for a job wherever the set speaks of it (its box, its row, a skip). */
export function jobName(job: { jobNumber: number; title: string }): string {
  return `#${job.jobNumber} ${job.title}`;
}

/**
 * `job.setServiceType`, as both pages send it: the type picked (`typeId`), and the write, with
 * what both do around it. A set that went through refetches everything it changed; a refused
 * one refetches the shop's list, since the usual refusal is a type turned off or deleted since
 * it loaded (`refetchServiceTypes`). `onSuccess` is the page's own part (spend the selection,
 * close the step).
 *
 *   - `send(jobs, typeId)`: the jobs by id with a fresh idempotency key, remembering them by
 *     name (`sent`) so a skip can name a job the server could not (`SetServiceTypeResult`).
 *   - `pick(typeId)`: a new pick; the last answer or refusal was about another one, so it goes.
 *   - `restart()`: ask afresh, with nothing picked and no answer left.
 *   - `forget()`: the last answer or refusal goes (the Jobs list, when its view changes).
 *
 * Nothing is forgotten while a set is on its way: `reset()` would detach the page from the write
 * in flight, turn its buttons back on, and lose the answer when it lands.
 */
export function useSetServiceType({ onSuccess }: { onSuccess: () => void }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [typeId, setTypeId] = useState('');
  const [sent, setSent] = useState<readonly NamedJob[]>([]);
  const mutation = useMutation(
    trpc.job.setServiceType.mutationOptions({
      onSuccess: () => {
        onSuccess();
        void qc.invalidateQueries();
      },
      onError: () => refetchServiceTypes(qc, trpc),
    }),
  );
  const forget = () => {
    if (!mutation.isPending) mutation.reset();
  };
  return {
    ...mutation,
    typeId,
    sent,
    forget,
    pick: (next: string) => {
      setTypeId(next);
      forget();
    },
    restart: () => {
      setTypeId('');
      forget();
    },
    send: (jobs: readonly NamedJob[], serviceTypeId: string) => {
      setSent(jobs);
      mutation.mutate({ jobIds: jobs.map((job) => job.id), serviceTypeId, ...idem() });
    },
  };
}

/**
 * What a set did, in words: how many jobs it changed, and EVERY job it left as it was, by name,
 * with the server's reason ("it is already Repair", "we couldn't find it"). A partial result
 * must never read as a full one (spec story 25): the two counts are separate, and each skip is
 * its own line. A job the server could not find it names from `sent`, the jobs the page sent
 * as the office saw them; the server names none it could not find (another shop's included).
 *
 * A LIVE REGION that is always there (`role="status"`), filled when an answer lands and emptied
 * when it is forgotten: a region inserted already filled may never be announced. So a set is
 * announced while focus stays where it was, a clean one included. No hooks.
 */
export function SetServiceTypeResult({
  result,
  sent = [],
}: {
  result: JobSetServiceTypeResult | undefined;
  sent?: readonly NamedJob[];
}) {
  const named = (jobId: string) => {
    const job = sent.find((j) => j.id === jobId);
    return job ? jobName(job) : 'A job';
  };
  return (
    <div role="status">
      {result ? (
        <section className="rounded-[var(--radius)] border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold">
            {result.changed.length > 0
              ? `Set ${result.changed.length} ${result.changed.length === 1 ? 'job' : 'jobs'} to ${result.serviceType.label}`
              : 'Nothing changed'}
            {result.skipped.length > 0
              ? ` · left ${result.skipped.length} as ${result.skipped.length === 1 ? 'it was' : 'they were'}`
              : ''}
          </h2>
          {result.skipped.length > 0 ? (
            <ul className="mt-2 space-y-1 text-sm">
              {result.skipped.map((skip) => (
                <li key={skip.jobId} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {skip.jobNumber === null
                      ? named(skip.jobId)
                      : jobName({ jobNumber: skip.jobNumber, title: skip.title ?? '' })}
                  </span>
                  <StatusBadge tone="neutral">{skip.why}</StatusBadge>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

/**
 * The job page's service type, as its meta strip shows it: the type's name (the dash for a job
 * from before service types), and, for owner and office, "Change", at every status (spec story
 * 26). A tech or a bookkeeper sees the name alone: the change needs `job:update`, as the server
 * says. `aria-disabled` and a guard while a change is on its way, never `disabled`, so the
 * button can take focus back when the step closes. No hooks.
 */
export function JobServiceTypeValue({
  label,
  canChange,
  open,
  pending,
  buttonRef,
  onToggle,
}: {
  label: string | null;
  canChange: boolean;
  /** The change step is open below: the button says so (`aria-expanded`). */
  open: boolean;
  pending: boolean;
  /** Where the step hands focus back when it closes. */
  buttonRef?: Ref<HTMLButtonElement>;
  onToggle: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      {dash(label)}
      {canChange ? (
        <Button
          variant="ghost"
          ref={buttonRef}
          aria-expanded={open}
          aria-disabled={pending}
          onClick={() => {
            if (!pending) onToggle();
          }}
        >
          Change
        </Button>
      ) : null}
    </span>
  );
}
